import React from 'react'

import { connect } from 'react-redux'
import { reduxForm } from 'redux-form'
import { appUpdateRequest } from '../../actions'
import { Prompt } from 'react-router-dom'

import { Box, Row, Column } from 'components/Layout'
import { Form, FormSection, FormError } from 'components/Form'
import { SaveButton, Button } from 'components/Button'
import { BoxShell } from 'components/Shells'

import { Image } from 'components/Image'

import { AppIsPendingReviewBanner } from '../../views/Banners'
// import // extensionUrlValidator
// extensionLogoUrlValidator
// './validators.js'
import FieldSection from './FieldSection.js'
import { TEXT, URL, APP_SCOPES } from './fieldTypes.js'

import oAuth from '../../assets/intros/girl-key.png'

const AppFeaturesOAuthClientForm = props => {
	const {
		dispatchSubmit,
		handleSubmit,
		submitting,
		dirty,
		app,
		change,
		error,
		readOnly,
		inReview,
		isSecretVisible,
		isBackendApp
	} = props

	const fields = [
		{
			type: TEXT,
			name: 'ssoSecret',
			fieldId: 'ssoSecret',
			title: 'Client Secret',
			description: (
				<div>
					A unique Client Secret for your app. You can use it to get{' '}
					<a
						href={
							process.env.REACT_APP_DEV_DOCS_URL +
							'/authorization/#4-exchange-code-for-access-token-and-refresh-token'
						}
						target="_blank"
						rel="noopener"
					>
						access and refresh tokens
					</a>. Keep the original hash in a safe place – It's not
					possible to recover it!
				</div>
			),
			warningMsg: isSecretVisible
				? "Copy and save Client Secret now! You won't be able to see it after page reloads."
				: null,
			highlighted: isSecretVisible,
			// disabled: true,
			readOnly: true,
			volatile: true,
			hide: !isBackendApp
		},
		{
			type: TEXT,
			name: 'ssoClientId',
			fieldId: 'ssoClientId',
			title: 'Client Id',
			description: (
				<div>
					A unique identifier of your app. You'll need this to
					authorize the requests. Go to{' '}
					<a
						href={
							process.env.REACT_APP_DEV_DOCS_URL +
							'/authorization/#public-web-apps'
						}
						target="_blank"
						rel="noopener"
					>
						docs
					</a>{' '}
					for samples.
				</div>
			),
			// disabled: true,
			readOnly: true
		},
		{
			type: URL,
			name: 'redirectUri',
			fieldId: 'redirectUri',
			title: 'Redirect URI',
			description: (
				<div>
					Redirect URI is the address of your app that will receive
					access_token in a URL. You can set up several allowed
					redirect URIs (separated with commas). See the{' '}
					<a
						href={
							process.env.REACT_APP_DEV_DOCS_URL +
							'/authorization/#public-web-apps'
						}
						target="_blank"
						rel="noopener"
					>
						docs
					</a>{' '}
					for more information. You can also use{' '}
					<a
						href={
							process.env.REACT_APP_DEV_DOCS_URL +
							'/sign-in-with-chatio'
						}
						target="_blank"
						rel="noopener"
					>
						Sign in with chat.io button
					</a>.
				</div>
			),
			placeholder: 'https://www.your.com/domain',
			//validate: extensionUrlValidator,
			required: true,
			disabled: readOnly
		},
		{
			type: APP_SCOPES,
			name: 'scopes',
			fieldId: 'scopes',
			title: 'Access scopes',
			description: (
				<div>
					Scopes is a list of permissions your app will get. Head to
					the{' '}
					<a
						href={
							process.env.REACT_APP_DEV_DOCS_URL +
							'/authorization/#public-web-apps'
						}
						target="_blank"
						rel="noopener"
					>
						docs
					</a>{' '}
					for more details.
				</div>
			),
			required: true,
			changeField: change,
			disabled: readOnly
		}
	]

	if (typeof app === 'undefined') {
		return (
			<Form>
				<BoxShell height={200} />
				<BoxShell height={140} />
			</Form>
		)
	}

	return (
		<Form handleSubmit={handleSubmit(dispatchSubmit)}>
			<Prompt
				when={submitting || dirty}
				message={() =>
					`You've got unsaved changes. Are you sure you want to leave this page?`
				}
			/>

			<AppIsPendingReviewBanner appId={app.id} inReview={inReview} />

			<Box
				header={'OAuth2.0 Client'}
				body={
					<span>
						<FormSection dark>
							<Row notPadded center>
								<Column width={4}>
									<Image
										src={oAuth}
										width={140}
										height={200}
										style={{ margin: '0 auto' }}
									/>
								</Column>
								<Column>
									<p>
										Use <strong>OAuth 2.0</strong> to
										securely access chat.io user's data.
									</p>
									<a
										href={
											process.env.REACT_APP_DEV_DOCS_URL +
											'/authorization/'
										}
										target="_blank"
										rel="noopener"
									>
										<Button type={'ghost'}>
											Learn more
										</Button>
									</a>
								</Column>
							</Row>
						</FormSection>
						<div>
							{fields.map(field => {
								return (
									<FieldSection
										{...field}
										key={field.fieldId}
									/>
								)
							})}
						</div>
					</span>
				}
				info={
					!readOnly ? (
						<span>
							{!submitting ? <FormError error={error} /> : null}
							<SaveButton submitting={submitting} dirty={dirty} />
						</span>
					) : (
						<span>App in review – read only</span>
					)
				}
				footer={
					!readOnly ? (
						<span>
							{!submitting ? <FormError error={error} /> : null}
							<SaveButton submitting={submitting} dirty={dirty} />
						</span>
					) : (
						<span>App in review – read only</span>
					)
				}
			/>
		</Form>
	)
}

const mapStateToProps = (state, ownProps) => {
	const appId = ownProps.match.params.appId
	const currentApp = state.apps.items[appId]
	const readOnly = currentApp ? currentApp.status !== 'IN_DEVELOPMENT' : false
	const inReview = currentApp ? currentApp.status === 'IN_REVIEW' : false
	const isBackendApp = currentApp
		? currentApp.clientId === 'chatio-backend'
		: false

	const currentSecret = state.apps.secrets[appId]
		? state.apps.secrets[appId].ssoSecret
		: undefined

	const isSecretVisible = currentSecret ? true : false

	if (currentSecret) {
		currentApp['ssoSecret'] = currentSecret
	}

	return {
		app: currentApp,
		readOnly,
		inReview,
		isSecretVisible,
		isBackendApp,
		initialValues: currentApp || {
			customData: {}
		}
	}
}

const mapDispatchToProps = (dispatch, ownProps) => {
	return {
		dispatchSubmit: values => {
			dispatch(
				appUpdateRequest({
					formName: 'appFeaturesOAuthClientForm',
					...values
				})
			)
		}
	}
}

const ConnectedAppFeaturesOAuthClientForm = connect(
	mapStateToProps,
	mapDispatchToProps
)(
	reduxForm({
		form: 'appFeaturesOAuthClientForm',
		touchOnBlur: false,
		enableReinitialize: true
	})(AppFeaturesOAuthClientForm)
)

export default ConnectedAppFeaturesOAuthClientForm



// WEBPACK FOOTER //
// ./src/containers/Forms/AppFeaturesOAuthClientForm.js